/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useLayoutEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';

interface OnboardingTooltipProps {
  step: number;
  totalSteps: number;
  onNext: () => void;
  onSkip: () => void;
  title: string;
  content: string;
  targetId: string;
  nextButtonText?: string;
}

const OnboardingTooltip: React.FC<OnboardingTooltipProps> = ({
  step,
  totalSteps,
  onNext,
  onSkip,
  title,
  content,
  targetId,
  nextButtonText: customNextButtonText
}) => {
  const { t } = useTranslation();
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  const nextButtonText = customNextButtonText || t('common_next');

  useLayoutEffect(() => {
    let retryCount = 0;
    const maxRetries = 10;
    const retryInterval = 100;
    let timeoutId: NodeJS.Timeout;

    const findAndSetup = () => {
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        const updateRect = () => {
          setTargetRect(targetElement.getBoundingClientRect());
        };
        updateRect();

        const observer = new ResizeObserver(updateRect);
        observer.observe(document.body);

        // Also listen for scroll to reposition
        window.addEventListener('scroll', updateRect, true);

        return () => {
          observer.disconnect();
          window.removeEventListener('scroll', updateRect, true);
        };
      } else if (retryCount < maxRetries) {
        retryCount++;
        timeoutId = setTimeout(findAndSetup, retryInterval);
      } else {
        console.warn(`Onboarding target element with id "${targetId}" not found after ${maxRetries} retries.`);
      }
    };

    const cleanup = findAndSetup();

    return () => {
      if (cleanup) cleanup();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [targetId]);

  const tooltipPosition: React.CSSProperties = {
    top: targetRect ? targetRect.bottom + 16 : '50%',
    left: targetRect ? targetRect.left + targetRect.width / 2 : '50%',
    transform: 'translateX(-50%)',
    position: 'fixed',
  };

  // Reposition tooltip if it overflows the viewport
  if (targetRect) {
    if (targetRect.bottom > window.innerHeight - 250) { // If tooltip might overflow bottom
      tooltipPosition.top = targetRect.top - 16;
      tooltipPosition.transform = 'translateX(-50%) translateY(-100%)';
    }
    if (targetRect.left + 160 > window.innerWidth) { // If tooltip might overflow right
      tooltipPosition.left = window.innerWidth - 160;
      tooltipPosition.transform = 'translateX(-50%)';
    }
    if (targetRect.left - 160 < 0) { // If tooltip might overflow left
      tooltipPosition.left = 160;
      tooltipPosition.transform = 'translateX(-50%)';
    }
  }


  return (
    <div className="fixed inset-0 z-[100]">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        style={{
          clipPath: targetRect
            ? `path(evenodd, 'M0 0 H ${window.innerWidth} V ${window.innerHeight} H 0 Z M ${targetRect.x - 8} ${targetRect.y - 8} H ${targetRect.x + targetRect.width + 8} V ${targetRect.y + targetRect.height + 8} H ${targetRect.x - 8} Z')`
            : 'none',
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      />

      {/* Tooltip Box */}
      {targetRect && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={tooltipPosition}
          className="absolute bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-80 p-5 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-bold font-serif text-gray-900 dark:text-white">{title}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 mb-4">{content}</p>
          <div className="flex justify-between items-center">
            <span className="text-xs font-bold text-gray-500 dark:text-gray-400">
              {step} / {totalSteps}
            </span>
            <div className="flex gap-2">
              <button onClick={onSkip} className="text-sm text-gray-500 hover:text-gray-800 dark:hover:text-white px-3 py-1.5 rounded-md">
                {t('onboarding_skip')}
              </button>
              <button onClick={onNext} className="bg-indigo-600 text-white font-semibold py-1.5 px-4 rounded-md text-sm hover:bg-indigo-700">
                {nextButtonText}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default OnboardingTooltip;